import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { getColor } from './statusColors';
import { escapeHtml } from './utils';

function createIcon(hausnummer, status, isSelected, unitBadge) {
  const color = getColor(status);
  const num = escapeHtml(hausnummer || '?');
  const w = isSelected ? 36 : 28;
  const h = isSelected ? 50 : 40;
  const fontSize = num.length > 3 ? 7 : num.length > 2 ? 8 : 10;
  const textY = Math.round(h * 0.42);

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 28 40">
    <path d="M14 0C6.27 0 0 6.27 0 14c0 9.33 14 26 14 26S28 23.33 28 14C28 6.27 21.73 0 14 0z"
      fill="${color}" stroke="white" stroke-width="1.5"/>
    <circle cx="14" cy="14" r="8" fill="rgba(0,0,0,0.15)"/>
    <text x="14" y="${textY}" text-anchor="middle" dominant-baseline="middle"
      font-family="Arial,sans-serif" font-size="${fontSize}" font-weight="700" fill="white"
      letter-spacing="-0.3">${num}</text>
  </svg>`;

  const badge = unitBadge
    ? `<div style="position:absolute;top:-8px;right:-12px;background:white;border:1.5px solid ${color};border-radius:8px;padding:1px 4px;font-size:9px;font-weight:700;color:${color};white-space:nowrap;line-height:1.4;box-shadow:0 1px 4px rgba(0,0,0,0.18);">${escapeHtml(unitBadge)}</div>`
    : '';

  return L.divIcon({
    html: `<div style="position:relative;display:inline-block;">${svg}${badge}</div>`,
    iconSize: [w, h],
    iconAnchor: [w / 2, h],
    className: '',
  });
}

function createHouseIcon(anzahlNe, status, isSelected) {
  const color = getColor(status);
  const size = isSelected ? 46 : 38;
  const ring = isSelected ? '3px solid white' : '2px solid white';

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 38 38">
    <polygon points="19,2 36,18 2,18" fill="${color}" stroke="white" stroke-width="1.5"/>
    <rect x="5" y="17" width="28" height="19" fill="${color}" stroke="white" stroke-width="1.5" rx="1"/>
    <rect x="14" y="25" width="10" height="11" fill="rgba(0,0,0,0.25)" rx="1"/>
    <circle cx="31" cy="8" r="7.5" fill="white" stroke="${color}" stroke-width="1.5"/>
    <text x="31" y="8" text-anchor="middle" dominant-baseline="middle"
      font-family="Arial,sans-serif" font-size="8" font-weight="700" fill="${color}">${escapeHtml(anzahlNe)}</text>
  </svg>`;

  return L.divIcon({
    html: `<div style="border:${ring};border-radius:4px;display:inline-block;">${svg}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    className: '',
  });
}

// Marker-Farbe bei Mehrfamilien-Gebäuden: offene Status zuerst — die Karte zeigt,
// wo noch Arbeit liegt; den Abschluss-Fortschritt zeigt das x/y-Badge
const UNIT_STATUS_PRIORITY = ['Interesse', 'Unstimmig', 'Nicht anwesend', 'Geclosed', 'Kein Interesse', 'Nicht schreibbar'];

function buildIcon(lead, isSelected) {
  const isHouse = (lead.anzahl_ne ?? 0) >= 3;
  const isMultiUnit = (lead.anzahl_ne ?? 0) >= 2;
  let status = lead.vertrieb_status ?? null;
  let closedUnits = 0;
  if (isMultiUnit && lead.unit_statuses) {
    try {
      const statuses = Object.values(JSON.parse(lead.unit_statuses))
        .map(v => (typeof v === 'string' ? v : v?.status))
        .filter(Boolean);
      closedUnits = statuses.filter(s => s === 'Geclosed').length;
      // Gebäude-Status konkurriert gleichberechtigt mit den Einheiten-Status
      if (status) statuses.push(status);
      const derived = UNIT_STATUS_PRIORITY.find(p => statuses.includes(p));
      if (derived) status = derived;
    } catch {}
  }
  const unitBadge = (!isHouse && isMultiUnit) ? (closedUnits > 0 ? `${closedUnits}/${lead.anzahl_ne}` : `${lead.anzahl_ne}`) : null;
  return isHouse
    ? createHouseIcon(closedUnits > 0 ? `${closedUnits}/${lead.anzahl_ne}` : lead.anzahl_ne, status, isSelected)
    : createIcon(lead.hausnummer, status, isSelected, unitBadge);
}

export default function MarkerCluster({ leads, selected, onSelect, onHouseClick }) {
  const map = useMap();
  const groupRef = useRef(null);
  const markersRef = useRef(new Map());   // lead.id → { marker, lead }
  const selectedIdRef = useRef(null);
  // Klick-Handler über Refs, damit neue Callbacks keinen Cluster-Neuaufbau auslösen
  const handlersRef = useRef({ onSelect, onHouseClick });
  useEffect(() => { handlersRef.current = { onSelect, onHouseClick }; });

  // Cluster nur neu aufbauen, wenn sich die Daten ändern — nicht bei Selektion
  useEffect(() => {
    if (groupRef.current) {
      map.removeLayer(groupRef.current);
    }
    markersRef.current = new Map();

    const group = L.markerClusterGroup({
      maxClusterRadius: zoom => zoom >= 18 ? 20 : 60,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      iconCreateFunction(cluster) {
        const count = cluster.getChildCount();
        const size = count > 100 ? 56 : count > 20 ? 46 : 38;
        const fontSize = count > 999 ? 11 : count > 99 ? 13 : 15;
        return L.divIcon({
          html: `
            <div style="
              width:${size}px;height:${size}px;border-radius:50%;
              background:linear-gradient(135deg,#4f46e5,#a855f7);
              border:2.5px solid white;
              box-shadow:0 4px 14px rgba(99,102,241,0.45);
              display:flex;align-items:center;justify-content:center;
              color:white;font-size:${fontSize}px;font-weight:700;
              font-family:-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif;
              text-shadow:0 1px 3px rgba(0,0,0,0.3);
            ">${count}</div>`,
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
          className: '',
        });
      },
    });

    const mappable = leads.filter(l => l.lat && l.lng);
    for (const lead of mappable) {
      const isMultiUnit = (lead.anzahl_ne ?? 0) >= 2;
      const marker = L.marker([lead.lat, lead.lng], {
        icon: buildIcon(lead, lead.id === selectedIdRef.current),
      });
      marker.on('click', () => {
        const h = handlersRef.current;
        return isMultiUnit ? h.onHouseClick(lead) : h.onSelect(lead);
      });
      markersRef.current.set(lead.id, { marker, lead });
      group.addLayer(marker);
    }

    map.addLayer(group);
    groupRef.current = group;

    return () => {
      map.removeLayer(group);
    };
  }, [leads, map]);

  // Selektion: nur die Icons des alten und neuen Markers tauschen
  useEffect(() => {
    const prevId = selectedIdRef.current;
    const newId = selected?.id ?? null;
    if (prevId === newId) return;
    selectedIdRef.current = newId;
    for (const id of [prevId, newId]) {
      const entry = markersRef.current.get(id);
      if (entry) entry.marker.setIcon(buildIcon(entry.lead, id === newId));
    }
  }, [selected]);

  return null;
}
